import { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowRight, Eye, EyeOff, ShieldCheck, BarChart3, Award } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { BRAND, SOLEMNITY_LOGO_URL } from "@/components/Logo";
import { useSeo } from "@/lib/seo";

const FEATURES = [
  { icon: ShieldCheck, title: "Secure proctored exams", desc: "Tab-switch & fullscreen monitoring keeps assessments fair." },
  { icon: BarChart3,   title: "Real-time analytics",    desc: "Track pass-rate, attempts and topic-level mastery." },
  { icon: Award,       title: "Verified certificates",  desc: "Earn shareable PDF certificates on completion." },
];

export default function Login() {
  useSeo({
    title: "Employee Login",
    description: "Sign in to the Solemnity Assessment Portal with your Employee ID or registered email.",
    path: "/login",
  });
  const { login, formatApiErrorDetail } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const submit = async (e) => {
    e?.preventDefault?.();
    setError("");
    setLoading(true);
    try {
      const u = await login(identifier, password);
      if (u.must_change_password) {
        navigate("/change-password", { replace: true });
        return;
      }
      const redirect = location.state?.from?.pathname || `/${u.role}`;
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="font-poppins min-h-screen w-full bg-[#F8FAFC] lg:grid lg:grid-cols-[1.05fr_1fr] xl:grid-cols-[1.2fr_1fr]">

      {/* ===== LEFT — premium brand panel ===== */}
      <aside className="relative hidden lg:flex overflow-hidden">
        {/* layered gradient: light gray → soft blue tint with royal/navy depth on the right edge */}
        <div className="absolute inset-0 bg-[linear-gradient(135deg,_#F8FAFC_0%,_#E0ECFF_45%,_#C7DCFF_75%,_#2563EB_120%)]" />
        {/* very subtle radial highlight + grid */}
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="absolute -top-32 -left-32 w-[520px] h-[520px] rounded-full bg-[#1E3A8A]/15 blur-3xl" />
        <div className="absolute bottom-[-200px] left-1/3 w-[520px] h-[520px] rounded-full bg-[#2563EB]/25 blur-3xl" />

        <div className="relative z-10 m-auto max-w-md xl:max-w-lg px-12 py-14">
          <div className="inline-flex items-center gap-2.5 mb-10 rounded-full bg-white/70 backdrop-blur-sm border border-white pl-1.5 pr-4 py-1.5 shadow-sm">
            <span className="w-7 h-7 rounded-full bg-white grid place-items-center border border-slate-200">
              <img src={SOLEMNITY_LOGO_URL} alt="" className="w-5 h-5 object-contain" />
            </span>
            <span className="text-[11px] tracking-[0.18em] uppercase text-[#1E3A8A] font-semibold">
              {BRAND.short} · IT Healthcare
            </span>
          </div>

          <h1 className="text-[40px] xl:text-[44px] leading-[1.08] font-semibold text-[#0F172A] tracking-tight">
            Assessment Portal for the modern workforce.
          </h1>
          <p className="mt-5 text-[15px] text-slate-600 leading-relaxed max-w-md">
            Evaluate skills. Build careers. Grow together — with a secure,
            enterprise-grade testing experience trusted across our teams.
          </p>

          <div className="mt-10 space-y-4">
            {FEATURES.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-3.5 bg-white/60 backdrop-blur-sm border border-white/80 rounded-2xl p-4 shadow-sm shadow-blue-900/5">
                <span className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#1E3A8A] to-[#2563EB] grid place-items-center text-white flex-shrink-0">
                  <Icon className="w-5 h-5" />
                </span>
                <div>
                  <div className="text-sm font-semibold text-[#0F172A]">{title}</div>
                  <div className="text-[13px] text-slate-600 leading-snug">{desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-6 left-12 right-12 z-10 flex items-center justify-between text-[11px] text-slate-600">
          <span>© 2026 {BRAND.full}</span>
          <div className="flex gap-5">
            <a href="#privacy" className="hover:text-[#1E3A8A]">Privacy</a>
            <a href="#terms" className="hover:text-[#1E3A8A]">Terms</a>
            <a href="#support" className="hover:text-[#1E3A8A]">Support</a>
          </div>
        </div>
      </aside>

      {/* ===== RIGHT — login card ===== */}
      <main className="relative flex items-center justify-center px-5 sm:px-8 py-10 sm:py-14 bg-white">
        {/* subtle decorative blob on mobile */}
        <div className="lg:hidden pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full bg-[#2563EB]/10 blur-3xl" />

        <div className="relative w-full max-w-[440px]">
          {/* LOGO — ~240px wide, never cropped */}
          <div className="flex flex-col items-center text-center">
            <img
              src={SOLEMNITY_LOGO_URL}
              alt="Solemnity"
              loading="eager"
              decoding="async"
              className="w-[240px] max-w-full h-auto object-contain select-none"
              draggable="false"
            />
            <div className="mt-3 text-[11px] sm:text-xs tracking-[0.22em] uppercase text-[#2563EB] font-semibold">
              Assessment Portal
            </div>
          </div>

          {/* CARD */}
          <div
            className="mt-8 bg-white border border-slate-200 p-7 sm:p-9"
            style={{
              borderRadius: 20,
              boxShadow: "0 24px 60px -25px rgba(30, 58, 138, 0.25), 0 8px 24px -12px rgba(15, 23, 42, 0.08)",
            }}
          >
            <h2 className="text-[26px] sm:text-[28px] leading-tight font-semibold text-[#0F172A] tracking-tight text-center">
              Employee Login
            </h2>
            <p className="mt-1.5 text-sm text-slate-500 text-center">
              Sign in using your Employee ID or registered email
            </p>

            <form onSubmit={submit} className="mt-7 space-y-4">
              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                  Employee ID / Email ID
                </label>
                <input
                  data-testid="login-identifier-input"
                  type="text"
                  autoComplete="username"
                  required
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  placeholder="EMP123456 or you@solemnity.com"
                  className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-4 py-3 text-[15px] text-slate-900 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB] outline-none transition"
                />
              </div>

              <div>
                <label className="block text-[13px] font-medium text-slate-700 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <input
                    data-testid="login-password-input"
                    type={showPw ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    className="w-full bg-[#F8FAFC] border border-slate-200 rounded-xl px-4 py-3 pr-12 text-[15px] text-slate-900 placeholder:text-slate-400 focus:bg-white focus:ring-2 focus:ring-[#2563EB]/30 focus:border-[#2563EB] outline-none transition"
                  />
                  <button
                    type="button"
                    data-testid="login-toggle-password"
                    onClick={() => setShowPw((v) => !v)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-500 hover:text-[#1E3A8A]"
                    aria-label={showPw ? "Hide password" : "Show password"}
                  >
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm pt-1">
                <label className="flex items-center gap-2 cursor-pointer text-slate-700">
                  <input
                    type="checkbox"
                    data-testid="login-remember"
                    checked={remember}
                    onChange={(e) => setRemember(e.target.checked)}
                    className="w-4 h-4 accent-[#2563EB]"
                  />
                  Remember me
                </label>
                <Link
                  to="/forgot-password"
                  className="text-[#2563EB] font-medium hover:text-[#1E3A8A] hover:underline"
                  data-testid="link-forgot"
                >
                  Forgot password?
                </Link>
              </div>

              {error && (
                <div
                  data-testid="login-error"
                  className="text-sm text-red-700 bg-red-50 border border-red-200 px-3 py-2.5 rounded-xl"
                >
                  {error}
                </div>
              )}

              <button
                data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="mt-1 w-full inline-flex items-center justify-center gap-2 text-white font-semibold tracking-tight px-4 py-3.5 rounded-xl transition disabled:opacity-60"
                style={{
                  background: "linear-gradient(135deg, #1E3A8A 0%, #2563EB 100%)",
                  boxShadow: "0 10px 24px -10px rgba(37, 99, 235, 0.55)",
                }}
              >
                {loading ? (
                  <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <>
                    Sign In <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </form>
          </div>

          <div className="mt-8 text-center text-[11px] text-slate-500 lg:hidden">
            © 2026 {BRAND.full}
          </div>
        </div>
      </main>
    </div>
  );
}
